import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import produce from 'immer';
import { useReviewPreviewPageStatus, useReviewPreviewParams } from 'screens/performanceReview/hooks/reviewPreviewHooks';
import useReviewFormParams from 'screens/performanceReview/hooks/useReviewFormParams';

import { REACT_QUERY_KEYS } from 'constants/reactQueryKeys';

import { getQAForm } from 'services/performanceReview';

const getData = async ({
  reviewId,
  employeeId,
  providerId,
  responseId,
  providerType,
  isPreview,
  previewSubmissionStatus,
}) => {
  try {
    const res = await getQAForm({
      reviewId,
      employeeId,
      providerId,
      responseId,
      providerType,
      isPreview,
      previewSubmissionStatus,
    });
    return res;
  } catch (err) {
    console.error('Error fetching goal form data : ', err);
  }
  return null;
};

export const useOverallQuestionsFormData = ({ reviewId, employeeId, providerId, responseId }) => {
  const { isPreview, providerType } = useReviewPreviewParams();
  const previewSubmissionStatus = useReviewPreviewPageStatus();

  return useQuery(
    [REACT_QUERY_KEYS.ReviewOverallFormData, reviewId, employeeId, providerId, responseId],
    () =>
      getData({
        reviewId,
        employeeId,
        providerId,
        responseId,
        providerType,
        isPreview,
        previewSubmissionStatus,
      }),
    { enabled: !!employeeId }
  );
};

export const useOverallFormMutation = ({ answerQuestion }) => {
  const queryClient = useQueryClient();
  const { isPreview } = useReviewPreviewParams();

  const { employeeId, providerId, reviewId, responseId } = useReviewFormParams();

  return useMutation(answerQuestion, {
    onMutate: async ({ feedbackText, feedbackTextHtml, selectedAnswerOptions, QNAQuestionsIndex }) => {
      // Cancel any outgoing refetches
      // (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({
        queryKey: [REACT_QUERY_KEYS.ReviewOverallFormData, reviewId, employeeId, providerId, responseId],
      });

      // Snapshot the previous value
      const questionData = queryClient.getQueryData([
        REACT_QUERY_KEYS.ReviewOverallFormData,
        reviewId,
        employeeId,
        providerId,
        responseId,
      ]);

      console.log({ questionData });
      // Optimistically update to the new value
      queryClient.setQueryData(
        [REACT_QUERY_KEYS.ReviewOverallFormData, reviewId, employeeId, providerId, responseId],
        (old) => {
          const updatedData = produce(old, (draft) => {
            // eslint-disable-next-line no-param-reassign
            draft.QNAQuestions[QNAQuestionsIndex] = {
              ...draft.QNAQuestions[QNAQuestionsIndex],
              feedbackText,
              feedbackTextHtml,
              selectedAnswerOptions,
            };
          });
          return updatedData;
        }
      );

      // Return a context object with the snapshotted value
      return { questionData };
    },
    // If the mutation fails,
    // use the context returned from onMutate to roll back
    onError: (err, newQuestion, context) => {
      queryClient.setQueryData(
        [REACT_QUERY_KEYS.ReviewOverallFormData, reviewId, employeeId, providerId, responseId],
        context.questionData
      );
    },
    // Always refetch after error or success:
    onSettled: () => {
      if (isPreview) {
        return;
      }
      queryClient.invalidateQueries({
        queryKey: [REACT_QUERY_KEYS.ReviewOverallFormData, reviewId, employeeId, providerId, responseId],
      });
      queryClient.invalidateQueries({
        queryKey: [REACT_QUERY_KEYS.ReviewFormFillingRatingData, reviewId, employeeId, providerId],
      });
      // used to refetch sidebar data
      queryClient.invalidateQueries({
        queryKey: [REACT_QUERY_KEYS.ReviewFormSidebar, employeeId, reviewId, providerId],
      });
    },
  });
};

export const useOverallFormQueryData = () => {
  const queryClient = useQueryClient();
  const { employeeId, providerId, reviewId, responseId } = useReviewFormParams();

  const questionData = queryClient.getQueryData([
    REACT_QUERY_KEYS.ReviewOverallFormData,
    reviewId,
    employeeId,
    providerId,
    responseId,
  ]);

  return questionData;
};
